import { createSlice, current } from "@reduxjs/toolkit";
import {
  getDrivers,
  postDriver,
  updateDriver,
  deleteDriver,
} from "./driverActions";

// Below this much movement (in degrees, ~1.1m) we treat the new GPS point as a
// duplicate / jitter and keep the previous heading instead of recomputing it.
// This avoids Math.atan2(0, 0) === 0 snapping the car to "east" on the rapid GPS
// doubles, and stops the heading swinging randomly when the driver is stationary.
const MIN_MOVE_DEG = 1e-5;

// Heading the car icon is rotated by. Keeps the existing orientation convention
// (atan2(-Δlat, Δlng), asset faces east at 0°) but corrects longitude for latitude
// and holds the last good angle when there is no meaningful movement.
function getDriverAngle(prev, next) {
  if (!next.latitude || !next.longitude || !prev.latitude || !prev.longitude) {
    return prev.angle ?? 0;
  }
  const dLat = next.latitude - prev.latitude;
  const dLng = next.longitude - prev.longitude;
  if (Math.abs(dLat) < MIN_MOVE_DEG && Math.abs(dLng) < MIN_MOVE_DEG) {
    return prev.angle ?? 0;
  }
  const x = dLng * Math.cos((prev.latitude * Math.PI) / 180);
  return (Math.atan2(-dLat, x) * 180) / Math.PI;
}

// A GPS fix this inaccurate (metres, as the phone reports it) is a garage or
// urban-canyon guess, not a position: moving the car there yanks it onto the
// wrong street and back. Known drivers keep their last good position instead.
const MAX_ACCURACY_M = 50;

// Drogo's heading is a compass course: 0 = north, clockwise. The car asset
// faces east at 0 and CSS rotate is clockwise, so the icon angle is heading
// minus 90, normalised to (-180, 180] like getDriverAngle's output.
function angleFromHeading(heading) {
  let angle = (heading - 90) % 360;
  if (angle > 180) angle -= 360;
  else if (angle <= -180) angle += 360;
  return angle;
}

// Which way the car points after this ping. The phone's compass heading wins
// when it is sent; Drogo omits it while the driver is stationary, so a missing
// heading on a driver that has had one means "not turning", keep the last
// angle. Only a driver that never had a heading falls back to deriving it from
// the last two points.
function nextAngle(prev, next) {
  if (typeof next.heading === "number" && Number.isFinite(next.heading)) {
    return angleFromHeading(next.heading);
  }
  if (prev.heading != null) {
    return prev.angle ?? 0;
  }
  return getDriverAngle(prev, next);
}

const initialState = {
  loading: false,
  error: null,
  success: false,
  drivers: {
    results: [],
  },
};

const driverSlice = createSlice({
  name: "driver",
  initialState,
  reducers: {
    resetError: (state, action) => {
      if (action?.payload) {
        delete state.error[action?.payload];
        if (Object.keys(state.error)?.length === 0) {
          state.error = null;
        }
      } else {
        state.error = null;
      }
      state.loading = false;
      state.success = false;
    },
    addSocketDriverLocation: (state, action) => {
      const currentDrivers = current(state.drivers.results) || [];
      const found = currentDrivers.find(
        (driver) => driver.id == action.payload.driver.id
      );
      // Server time (ms) of this ping. Two backend pods can deliver out of
      // sequence, so a ping older than the last accepted one is dropped: it
      // would glide the car backwards and flip its heading.
      const ts = action?.payload?.ts ?? null;
      if (found && ts != null && found?.ts != null && ts < found.ts) {
        return;
      }
      // A poor fix never moves a car we already have a position for.
      const accuracy = action?.payload?.driver?.accuracy;
      if (found && typeof accuracy === "number" && accuracy > MAX_ACCURACY_M) {
        return;
      }
      state.drivers.results = found
        ? currentDrivers.map((driver) => {
            return driver.id == action.payload.driver.id
              ? {
                  ...driver,
                  longitude: action.payload.driver.longitude,
                  latitude: action.payload.driver.latitude,
                  speed: action.payload.driver.speed,
                  // Last known compass heading. Drogo omits it while the driver
                  // is stationary, so a null ping keeps the previous value.
                  heading: action.payload.driver.heading ?? driver.heading ?? null,
                  accuracy: accuracy ?? null,
                  angle: nextAngle(found, action.payload.driver),
                  ts,
                }
              : driver;
          })
        : [...currentDrivers, { ...action?.payload?.driver, ts }];
      // state.driverLocation = current(state.driverLocation).filter(driver => driver.id != action.payload.driver.id).push(action.payload.driver);
    },
    resetDriverSuccess: (state) => {
      state.success = false;
    },
    resetDrivers: (state) => {
      state.drivers = { results: [] };
      state.loading = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDrivers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDrivers.fulfilled, (state, action) => {
        state.loading = false;
        state.drivers = action.payload;
      })
      .addCase(getDrivers.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(postDriver.pending, (state) => {
        state.loading = true;
      })
      .addCase(postDriver.fulfilled, (state, action) => {
        const newObj = {
          meta: state.drivers?.meta,
          results: [action.payload, ...state.drivers?.results],
        };
        state.drivers = newObj;
        state.loading = false;
        state.success = true;
      })
      .addCase(postDriver.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(updateDriver.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        const updatedDrivers = state.drivers?.results.map((el) => {
          if (el?.id === action.payload.id) {
            return action.payload;
          } else {
            return el;
          }
        });
        const newObj = {
          meta: state.drivers?.meta,
          results: updatedDrivers,
        };
        state.drivers = newObj;
        state.loading = false;
        state.success = true;
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(deleteDriver.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDriver.fulfilled, (state, action) => {
        const { id } = action.payload;
        const updatedDrivers = state.drivers?.results.filter(
          (el) => el.id !== id
        );
        const newObj = {
          meta: state.drivers?.meta,
          results: updatedDrivers,
        };
        state.drivers = newObj;
        state.loading = false;
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});
export const { resetError, addSocketDriverLocation, resetDriverSuccess, resetDrivers } =
  driverSlice.actions;
export default driverSlice.reducer;
